import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '@euroasiann/api-client';
import { TicketEscalationLevel, TicketStatus } from '@euroasiann/shared';
import { PageSkeleton } from '@euroasiann/shared/ui';

// Duplicate Badge
const TicketStatusBadge = ({ status }: { status: string }) => {
    const getStatusColor = (status: string) => {
        switch (status) {
            case TicketStatus.OPEN: return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-100';
            case TicketStatus.IN_PROGRESS: return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-100';
            case TicketStatus.RESOLVED: return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-100';
            case TicketStatus.CLOSED: return 'bg-[#0088cc]/10 text-[hsl(var(--foreground))] dark:bg-[#0088cc]/18/40 dark:text-[hsl(var(--foreground))]';
            case TicketStatus.ESCALATED: return 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-100';
            default: return 'bg-[#0088cc]/10 text-[hsl(var(--foreground))] dark:bg-[#0088cc]/18/40 dark:text-[hsl(var(--foreground))]';
        }
    };
    return (
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(status)}`}>
            {status?.replace?.('_', ' ') || status || 'N/A'}
        </span>
    );
};

export const TicketDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [ticket, setTicket] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [note, setNote] = useState('');
    const [processing, setProcessing] = useState(false);

    const fetchTicket = async () => {
        try {
            const response = await apiClient.getSupportTicketById('admin', id!);
            
            // Robust multi-layered mapping as requested by user
            const ticketData = 
                response?.data?.data?.ticket ?? 
                response?.data?.data ?? 
                response?.data ?? 
                {};
                
            setTicket(ticketData);
        } catch (error) {
            navigate('/support/tickets');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (id) fetchTicket();
    }, [id]);

    const handleAction = async (action: 'resolve' | 'escalate') => {
        setProcessing(true);
        try {
            if (action === 'resolve') {
                await apiClient.resolveSupportTicket('admin', id!, note);
            } else {
                await apiClient.escalateSupportTicket('admin', id!, note);
            }
            fetchTicket();
            setNote('');
        } catch (error) {
            console.error(error);
            alert('Action failed');
        } finally {
            setProcessing(false);
        }
    };

    if (isLoading || !ticket) return <PageSkeleton variant="cards" />;

    const canEscalate = ticket.escalationLevel === TicketEscalationLevel.CENTRAL_ADMIN &&
        ticket.status !== TicketStatus.RESOLVED &&
        ticket.status !== TicketStatus.CLOSED;

    const canResolve = ticket.escalationLevel === TicketEscalationLevel.CENTRAL_ADMIN &&
        ticket.status !== TicketStatus.RESOLVED &&
        ticket.status !== TicketStatus.CLOSED;

    return (
        <div className="w-full space-y-6">
            <div className="flex items-center justify-between">
                <button
                    onClick={() => navigate('/support/tickets')}
                    className="flex items-center gap-2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
                >
                    &larr; Back to Tickets
                </button>
                <div className="flex gap-2">
                    {canResolve && (
                        <button
                            onClick={() => handleAction('resolve')}
                            disabled={processing}
                            className="bg-emerald-600 text-white px-5 py-2.5 rounded-xl font-semibold shadow-sm hover:bg-emerald-700 disabled:opacity-50 transition-all"
                        >
                            Resolve Ticket
                        </button>
                    )}
                    {canEscalate && (
                        <button
                            onClick={() => handleAction('escalate')}
                            disabled={processing}
                            className="bg-orange-600 text-white px-5 py-2.5 rounded-xl font-semibold shadow-sm hover:bg-orange-700 disabled:opacity-50 transition-all"
                        >
                            Escalate to Tech Team
                        </button>
                    )}
                </div>
            </div>

            <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm p-6 sm:p-8">
                <div className="flex flex-col sm:flex-row justify-between items-start border-b border-[hsl(var(--border))] pb-6 mb-6 gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-[hsl(var(--foreground))] mb-2">{ticket.title}</h1>
                        <div className="text-sm text-[hsl(var(--muted-foreground))] flex items-center gap-2">
                            Created by <span className="font-medium text-[hsl(var(--foreground))]">{ticket.createdBy?.firstName} {ticket.createdBy?.lastName}</span> ({ticket.organizationId?.name})
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <TicketStatusBadge status={ticket.status} />
                        <span className="text-xs font-mono text-[hsl(var(--muted-foreground))] px-2 py-1 bg-[hsl(var(--secondary))] rounded">
                            #{ticket?._id?.slice?.(-6).toUpperCase() || 'N/A'}
                        </span>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="p-4 rounded-lg bg-[hsl(var(--secondary))] border border-[hsl(var(--border))]">
                        <label className="text-xs font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider block mb-1">Priority</label>
                        <p className="capitalize font-semibold text-[hsl(var(--foreground))]">{ticket.priority}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-[hsl(var(--secondary))] border border-[hsl(var(--border))]">
                        <label className="text-xs font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider block mb-1">Current Level</label>
                        <p className="capitalize font-semibold text-[hsl(var(--foreground))]">{ticket?.escalationLevel?.replace?.('_', ' ') || ticket?.escalationLevel || 'N/A'}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-[hsl(var(--secondary))] border border-[hsl(var(--border))]">
                        <label className="text-xs font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider block mb-1">Created At</label>
                        <p className="font-semibold text-[hsl(var(--foreground))]">{ticket?.createdAt ? new Date(ticket.createdAt).toLocaleString() : 'N/A'}</p>
                    </div>
                </div>

                <div>
                    <h3 className="text-sm font-semibold text-[hsl(var(--foreground))] uppercase tracking-wider mb-2">Description</h3>
                    <div className="p-6 rounded-xl bg-[hsl(var(--secondary))] text-[hsl(var(--foreground))] border border-[hsl(var(--border))] whitespace-pre-wrap leading-relaxed">
                        {ticket.description}
                    </div>
                </div>
            </div>

            {(canResolve || canEscalate) && (
                <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm p-6">
                    <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2 pl-1">Resolution / Escalation Note</label>
                    <textarea
                        className="w-full border border-[hsl(var(--border))] rounded-xl p-4 bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-transparent transition-all"
                        placeholder="Add a note explaining the action..."
                        rows={3}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                    />
                </div>
            )}

            <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm p-6">
                <h3 className="text-lg font-bold text-[hsl(var(--foreground))] mb-6">Activity History</h3>
                <div className="space-y-6">
                    {(ticket?.history || []).slice().reverse().map((item: any, i: number) => (
                        <div key={i} className="flex gap-4 border-l-2 border-[hsl(var(--border))] pl-4 relative">
                            {/* Dot */}
                            <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-[hsl(var(--border))] border-4 border-[hsl(var(--card))]" />

                            <div className="text-xs text-[hsl(var(--muted-foreground))] min-w-[100px] pt-0.5">
                                {new Date(item.timestamp).toLocaleString()}
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="font-bold text-sm text-[hsl(var(--foreground))]">{item.performedBy?.firstName} {item.performedBy?.lastName}</span>
                                    <span className="text-[10px] px-1.5 py-0.5 rounded uppercase font-bold bg-[hsl(var(--secondary))] text-[hsl(var(--foreground))] border border-[hsl(var(--border))]">
                                        {item.action}
                                    </span>
                                </div>
                                <p className="text-[hsl(var(--muted-foreground))] text-sm bg-[hsl(var(--secondary))]/50 p-3 rounded-lg border border-[hsl(var(--border))]/50">
                                    {item.description}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
